/**
 * S2 · The Skip (#skip) — THE FEED homepage's signature (FINAL-SPEC §4 S2).
 *
 * A timed, tap-driven one-shot (never scroll-pinned): when the phone is in
 * view, a B&W "commercial" counts down "Skip in 3·2·1", the Skip button
 * enables, and — if the visitor doesn't tap it — a hand cursor glides in and
 * taps it. A yellow + black double slab slices the ad away, a real AFAWY reel
 * snaps in and plays, the like counter rolls, the caption line re-pops, and a
 * Replay button appears (WCAG 2.2.2: the whole run is < 5s and replayable).
 *
 * The markup is the FINISHED state. Everything here only adds the from-states
 * (`.is-armed` …) when motion is allowed; under reduced motion / no JS the
 * page shows the reel poster and the Skip button simply plays it.
 */
import { ensureSrc, pauseReel, playReel } from "./runtime";

const STATES = [
  "is-armed",
  "is-run",
  "is-ready",
  "is-cursor",
  "is-tap",
  "is-skipped",
  "is-done",
  "is-playing",
];

type Phase = "armed" | "running" | "ready" | "skipped" | "done";

export function init(): void {
  const sec = document.querySelector<HTMLElement>("#skip.skip");
  const phone = sec?.querySelector<HTMLElement>(".phone--skip");
  const btn = sec?.querySelector<HTMLButtonElement>(".skip__btn");
  if (!sec || !phone || !btn) return;
  const replay = sec.querySelector<HTMLButtonElement>(".skip__replay");
  const video = sec.querySelector<HTMLVideoElement>(".skip__video");
  const line = sec.querySelector<HTMLElement>(".skip__line");
  const num = sec.querySelector<HTMLElement>(".skip__n");
  const tc = sec.querySelector<HTMLElement>(".skip__tc");

  if (!document.documentElement.classList.contains("js-motion")) {
    initStatic(sec, phone, btn, video);
    return;
  }

  buildOdometer(sec);
  // the caption line stays plain until the skip: marking it revealed now
  // keeps the shared reveal from popping it on entry (its pop is the punchline)
  line?.classList.add("in");

  let phase: Phase = "armed";
  let ratio = 0;
  let timers: number[] = [];
  let focusAfter = false;

  const at = (ms: number, fn: () => void): void => {
    timers.push(window.setTimeout(fn, ms));
  };
  const clearTimers = (): void => {
    for (const id of timers) window.clearTimeout(id);
    timers = [];
  };
  const setCount = (n: number): void => {
    if (num) num.textContent = String(n);
    if (tc) tc.textContent = `0:0${n}`;
  };
  /** Apply a class change with every transition/animation off for a frame. */
  const instantly = (fn: () => void): void => {
    sec.classList.add("is-instant");
    fn();
    void sec.offsetWidth;
    window.requestAnimationFrame(() => sec.classList.remove("is-instant"));
  };
  const needRatio = (): number => {
    // the phone is shorter than most viewports; on a short landscape screen
    // it can never reach 60%, so ask for most of what can be seen
    const h = phone.getBoundingClientRect().height || 1;
    return Math.min(0.6, (0.9 * window.innerHeight) / h);
  };
  const syncVideo = (): void => {
    if (!video || (phase !== "skipped" && phase !== "done")) return;
    if (ratio >= 0.35) {
      if (playReel(video)) sec.classList.add("is-playing");
    } else if (!video.paused) {
      pauseReel(video);
    }
  };

  const arm = (): void => {
    phase = "armed";
    sec.classList.add("is-armed");
    btn.setAttribute("aria-disabled", "true");
    setCount(3);
  };

  const run = (): void => {
    if (phase !== "armed") return;
    phase = "running";
    sec.classList.add("is-run");
    at(1000, () => setCount(2));
    at(2000, () => setCount(1));
    at(3000, () => {
      setCount(0);
      phase = "ready";
      sec.classList.add("is-ready");
      btn.removeAttribute("aria-disabled");
    });
    at(3800, () => sec.classList.add("is-cursor"));
    at(4500, () => {
      sec.classList.add("is-tap");
      skip();
    });
  };

  const skip = (): void => {
    if (phase !== "ready") return;
    clearTimers();
    phase = "skipped";
    focusAfter = document.activeElement === btn;
    sec.classList.add("is-skipped");
    btn.setAttribute("aria-disabled", "true");
    if (video) ensureSrc(video);
    at(120, syncVideo);
    at(400, () => {
      if (line) popCaptions(line);
    });
    at(1600, finish);
  };

  const finish = (): void => {
    phase = "done";
    sec.classList.add("is-done");
    if (replay) {
      replay.hidden = false;
      if (focusAfter) replay.focus({ preventScroll: true });
    }
    focusAfter = false;
  };

  /** The phone left the screen mid-run: land on the finished state quietly. */
  const finishSilently = (): void => {
    clearTimers();
    instantly(() => {
      sec.classList.remove("is-cursor", "is-tap");
      sec.classList.add("is-run", "is-ready", "is-skipped", "is-done");
      setCount(0);
    });
    // no pill run off-screen: the key word simply lands marked
    line
      ?.querySelector<HTMLElement>(".w[data-key]")
      ?.classList.add("is-key-on");
    phase = "done";
    btn.setAttribute("aria-disabled", "true");
    if (replay) replay.hidden = false;
    if (video) ensureSrc(video);
  };

  const restart = (): void => {
    clearTimers();
    const hadFocus = document.activeElement === replay;
    if (video) {
      pauseReel(video);
      try {
        video.currentTime = 0;
      } catch {
        /* not loaded yet */
      }
    }
    instantly(() => {
      sec.classList.remove(...STATES);
      arm();
    });
    if (line) clearCaptions(line);
    if (replay) replay.hidden = true;
    if (hadFocus) btn.focus({ preventScroll: true });
    // let the reset frame paint, then run (if the phone is still in view)
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        if (phase === "armed" && ratio >= needRatio()) run();
      }),
    );
  };

  btn.addEventListener("click", () => {
    if (btn.getAttribute("aria-disabled") === "true") return;
    skip();
  });
  replay?.addEventListener("click", restart);
  // after the skip a tap on the screen plays/pauses the reel (Save-Data etc.)
  phone.addEventListener("click", () => {
    if (!video || phase !== "done") return;
    if (video.paused) {
      ensureSrc(video);
      video.muted = true;
      void video
        .play()
        .then(() => sec.classList.add("is-playing"))
        .catch(() => {});
    } else {
      video.pause();
    }
  });

  arm();

  if (!("IntersectionObserver" in window)) {
    finishSilently();
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        if (phase === "armed" && ratio >= needRatio()) run();
        else if ((phase === "running" || phase === "ready") && ratio < 0.2)
          finishSilently();
        syncVideo();
      }
    },
    { threshold: [0, 0.1, 0.2, 0.3, 0.35, 0.45, 0.6, 0.75, 1] },
  );
  io.observe(phone);
}

/** Reduced motion / no motion: the finished state; Skip (or the screen) plays
 * the reel — a user gesture, so it may play even when nothing autoplays. */
function initStatic(
  sec: HTMLElement,
  phone: HTMLElement,
  btn: HTMLButtonElement,
  video: HTMLVideoElement | null,
): void {
  if (!video) return;
  // here the button plays/pauses the reel, so it is named for what it does
  // now (the visible face keeps the slide's "Skip ad" wording)
  const playName = btn.getAttribute("data-aria-play");
  const pauseName = btn.getAttribute("data-aria-pause");
  const name = (): void => {
    const next = video.paused ? playName : pauseName;
    if (next) btn.setAttribute("aria-label", next);
  };
  name();
  video.addEventListener("play", name);
  video.addEventListener("pause", name);
  const play = (): void => {
    ensureSrc(video);
    video.muted = true;
    void video
      .play()
      .then(() => sec.classList.add("is-playing"))
      .catch(() => {});
  };
  btn.addEventListener("click", () => {
    if (video.paused) play();
    else video.pause();
  });
  phone.addEventListener("click", () => {
    if (video.paused) play();
    else video.pause();
  });
  if ("IntersectionObserver" in window) {
    new IntersectionObserver((entries) => {
      for (const entry of entries)
        if (!entry.isIntersecting && !video.paused) video.pause();
    }).observe(phone);
  }
}

/** The like counter rolls 1,248 → 1,311: one 0–9,0–9 strip per digit, each
 * resting on its "from" digit while armed and on its "to" digit after. */
function buildOdometer(sec: HTMLElement): void {
  const likes = sec.querySelector<HTMLElement>(".skip__likes");
  const odo = likes?.querySelector<HTMLElement>(".skip__odo");
  if (!likes || !odo) return;
  const from = likes.getAttribute("data-from") ?? "";
  const to = likes.getAttribute("data-to") ?? "";
  if (from.length !== to.length) return;
  let di = 0;
  for (let i = 0; i < to.length; i += 1) {
    const t = to[i] ?? "";
    const f = from[i] ?? "";
    if (!/[0-9]/.test(t) || !/[0-9]/.test(f)) {
      const s = document.createElement("span");
      s.textContent = t;
      odo.append(s);
      continue;
    }
    const fd = Number(f);
    const td = Number(t);
    const d = document.createElement("span");
    d.className = "skip__odo-d";
    const strip = document.createElement("span");
    strip.className = "skip__odo-strip";
    for (let n = 0; n < 20; n += 1) {
      const s = document.createElement("span");
      s.textContent = String(n % 10);
      strip.append(s);
    }
    strip.style.setProperty("--from", String(fd));
    strip.style.setProperty("--to", String(td >= fd ? td : td + 10));
    strip.style.setProperty("--di", String(di));
    di += 1;
    d.append(strip);
    odo.append(d);
  }
  sec.classList.add("has-odo");
}

function clearCaptions(head: HTMLElement): void {
  head.querySelector(".cap-pill")?.remove();
  for (const w of head.querySelectorAll(".w.is-key-on"))
    w.classList.remove("is-key-on");
}

/** Re-run the caption pop on the line after the skip; the karaoke pill steps
 * word to word and parks on the key word ("skip" / "يطوفون"). */
function popCaptions(head: HTMLElement): void {
  const words = Array.from(head.querySelectorAll<HTMLElement>(".w"));
  if (words.length === 0) return;
  clearCaptions(head);
  // drop the words to their from-state with no transition, then pop them
  head.classList.add("is-reset");
  head.classList.remove("in");
  void head.offsetWidth;
  head.classList.remove("is-reset");
  void head.offsetWidth;
  head.classList.add("in");

  const pill = document.createElement("span");
  pill.className = "cap-pill";
  pill.setAttribute("aria-hidden", "true");
  head.prepend(pill);
  const step = Math.max(
    70,
    parseFloat(head.style.getPropertyValue("--cap-stagger")) || 55,
  );
  // the pill's own untransformed box is the origin (its inline-start is the
  // RIGHT edge on /ar), so measure it instead of assuming the left edge —
  // as an offset from the line, because the page may scroll (or reflow)
  // between steps and a viewport rect read now goes stale
  const pill0 = pill.getBoundingClientRect();
  const head0 = head.getBoundingClientRect();
  // measured from the line's inline-start edge, so a re-wrap that changes
  // the line's width keeps the origin right in rtl too
  const rtl = getComputedStyle(pill).direction === "rtl";
  const offX = rtl ? pill0.right - head0.right : pill0.left - head0.left;
  const offY = pill0.top - head0.top;
  const keyAt = words.findIndex((w) => w.hasAttribute("data-key"));
  const last = keyAt >= 0 ? keyAt : words.length - 1;
  // rects are in viewport px, but the pill moves in its own (zoomed ≥2000px,
  // --ui-zoom) CSS px: convert with the pill measured against its 100px CSS width
  const z = pill0.width / 100 || 1;
  const place = (w: HTMLElement): void => {
    // the line and the word are read in the same frame, so scrolling
    // mid-run can never leave the pill parked away from its word
    const h0 = head.getBoundingClientRect();
    const base = {
      left: rtl ? h0.right + offX - pill0.width : h0.left + offX,
      top: h0.top + offY,
    };
    const r = w.getBoundingClientRect();
    const h = r.height / z;
    const padX = 0.12 * h;
    pill.style.setProperty("block-size", `${h * 0.9}px`);
    pill.style.transform = `translate(${(r.left - base.left) / z - padX}px, ${
      (r.top - base.top) / z + h * 0.06
    }px) scaleX(${(r.width / z + padX * 2) / 100})`;
  };
  let i = 0;
  const tick = (): void => {
    const w = words[i];
    if (!w || !head.isConnected) return;
    place(w);
    pill.classList.add("is-on");
    if (i >= last) {
      if (keyAt >= 0) {
        w.classList.add("is-key-on");
        parkOnResize(head, pill, w, place);
      } else pill.classList.remove("is-on");
      return;
    }
    i += 1;
    window.setTimeout(tick, step);
  };
  window.setTimeout(tick, 80);
}

/** Once parked, keep the pill on its key word when the line reflows (a
 * resize re-wraps it); stops as soon as the pill is removed (Replay). */
function parkOnResize(
  head: HTMLElement,
  pill: HTMLElement,
  word: HTMLElement,
  place: (w: HTMLElement) => void,
): void {
  if (!("ResizeObserver" in window)) return;
  let first = true;
  const ro = new ResizeObserver(() => {
    if (first) {
      first = false; // the initial callback: nothing has moved yet
      return;
    }
    if (!pill.isConnected) {
      ro.disconnect();
      return;
    }
    place(word);
  });
  ro.observe(head);
}
